import { useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Tooltip,
  Toolbar,
  Badge,
} from '@mui/material'
import {
  Home,
  Explore,
  Map,
  AutoStories,
  FolderSpecial,
  Leaderboard,
  EmojiEvents,
  People,
  SupportAgent,
  Build,
  QrCodeScanner,
  CardGiftcard,
  PermMedia,
  Person,
  Hiking,
  Groups2,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { useSupportSummary } from '@features/support/hooks/useSupportSummary'
import { ScanCodeDialog } from '@components/common/ScanCodeDialog'

export const SIDEBAR_WIDTH_FULL = 240
export const SIDEBAR_WIDTH_ICONS = 64
export const SIDEBAR_WIDTH = SIDEBAR_WIDTH_FULL // For backwards compatibility

// Sidebar collapse modes: 0 = hidden, 1 = icons only, 2 = full
export type SidebarCollapseMode = 0 | 1 | 2

interface NavItem {
  label: string
  /** Route to link to. Omitted for action items (e.g. scan code). */
  path?: string
  icon: React.ReactNode
  requiresAuth?: boolean
  requiresAdmin?: boolean
  requiresModerator?: boolean
  /** Renders as a button that performs an action instead of navigating. */
  action?: 'scan'
  /** Render a divider after this item. */
  dividerAfter?: boolean
  /** Show a count badge on the icon, from the named source. */
  badge?: 'support'
}

interface SidebarProps {
  mobileOpen?: boolean
  onDrawerToggle?: () => void
  isMobile?: boolean
  collapseMode?: SidebarCollapseMode
  onCollapseModeChange?: (mode: SidebarCollapseMode) => void
}

// Guest navigation items (not authenticated)
const guestNavItems: NavItem[] = [
  {
    label: 'Welcome Center',
    path: '/',
    icon: <Home />,
  },
  {
    label: 'About Us',
    path: '/about',
    icon: <Explore />,
  },
  {
    label: 'Features',
    path: '/features',
    icon: <EmojiEvents />,
  },
  {
    label: 'Build Adventures',
    path: '/build',
    icon: <Build />,
  },
]

// Authenticated user navigation items. Dividers and the Scan Code action are
// data-driven (dividerAfter / action) so ordering changes don't require
// recomputing magic indices in the render.
// Grouped and ordered to match the phone's drawer, so the two do not teach different maps of the
// same app. The reasoning behind each group is the app's, not this file's:
//
//  - Trail Management sits directly under My Adventures. A trail is a thing you have, and looking
//    for it anywhere else meant scanning the group below for a word that reads like the one above.
//  - Buddies and Groups are two destinations. They used to be one "Social" entry holding two tabs,
//    but people and groups are different things you go looking for, and neither should be behind
//    a tab on the way to the other.
//  - Media Manager and Support are the utility drawer, pinned last. Neither is somewhere
//    you go to play, and both are easier to find at a fixed spot at the bottom than shuffled among
//    the things you use daily.
//
// The phone has one entry this does not: Offline, which holds what is saved to that device. There
// is nothing for it to point at here.
const authenticatedNavItems: NavItem[] = [
  {
    label: 'Base Camp',
    path: '/',
    icon: <Home />,
  },
  // Adventure Trails hidden for now.
  // {
  //   label: 'Adventure Trails',
  //   path: '/trails',
  //   icon: <Explore />,
  // },
  {
    label: 'Discover Adventures',
    path: '/adventures',
    icon: <Map />,
  },
  {
    label: 'Discover Stories',
    path: '/stories',
    icon: <AutoStories />,
  },
  {
    label: 'My Adventures',
    path: '/my-adventures',
    icon: <FolderSpecial />,
  },
  {
    label: 'Trail Management',
    path: '/trail-recorder',
    icon: <Hiking />,
    dividerAfter: true,
  },
  {
    label: 'Profile',
    path: '/profile',
    icon: <Person />,
  },
  {
    label: 'Trail Buddies',
    path: '/buddies',
    icon: <People />,
  },
  {
    label: 'Groups',
    path: '/groups',
    icon: <Groups2 />,
    dividerAfter: true,
  },
  {
    label: 'Leaderboard',
    path: '/leaderboard',
    icon: <Leaderboard />,
  },
  {
    label: 'Scan Code',
    action: 'scan',
    icon: <QrCodeScanner />,
  },
  {
    label: 'My Rewards',
    path: '/rewards',
    icon: <CardGiftcard />,
    dividerAfter: true,
  },
  {
    label: 'Media Manager',
    path: '/media',
    icon: <PermMedia />,
  },
  // Always here. It used to be a moderator-only "Ticket Management" that appeared only while the
  // queue had something in it; now it is everyone's tickets and suggestions, with Moderation as a tab
  // for those who moderate. The badge counts only what is waiting on the viewer.
  {
    label: 'Support',
    path: '/support',
    icon: <SupportAgent />,
    badge: 'support',
  },
]

export function Sidebar({
  mobileOpen = false,
  onDrawerToggle,
  isMobile = false,
  collapseMode = 2,
  onCollapseModeChange,
}: SidebarProps) {
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore()
  const isAdmin = user?.roles?.includes('Admin')
  const isModerator = user?.roles?.includes('Moderator') || isAdmin
  const [scanCodeOpen, setScanCodeOpen] = useState(false)
  const { data: supportSummary } = useSupportSummary()

  const isIconsOnly = collapseMode === 1
  const isHidden = collapseMode === 0
  const currentWidth = isIconsOnly ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  // Choose navigation items based on authentication status
  const navItems = isAuthenticated ? authenticatedNavItems : guestNavItems

  // Filter admin and moderator items if needed
  const filteredNavItems = navItems.filter((item) => {
    if (item.requiresAdmin && !isAdmin) return false
    if (item.requiresModerator && !isModerator) return false
    return true
  })

  // Shared styling for a nav button (link or action).
  const navButtonSx = {
    borderRadius: 3,
    mb: 0.5,
    justifyContent: isIconsOnly ? 'center' : 'flex-start',
    px: isIconsOnly ? 1.5 : 2,
    transition: 'all 0.2s ease',
    '&.Mui-selected': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': {
        backgroundColor: 'primary.dark',
      },
      '& .MuiListItemIcon-root': {
        color: 'primary.contrastText',
      },
    },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  }

  const drawerContent = (
    <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <List sx={{ px: isIconsOnly ? 0.5 : 1, pt: 2, flex: 1 }}>
        {filteredNavItems.map((item) => {
          const isLink = !!item.path
          const isActive =
            isLink &&
            (location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(`${item.path}/`)))

          const handleActionClick = () => {
            if (item.action === 'scan') setScanCodeOpen(true)
            if (isMobile) onDrawerToggle?.()
          }

          const inner = (
            <>
              <ListItemIcon
                sx={{
                  color: isActive ? 'primary.contrastText' : 'primary.main',
                  minWidth: isIconsOnly ? 'auto' : 40,
                }}
              >
                {item.badge === 'support' ? (
                  <Badge
                    badgeContent={supportSummary?.attentionCount ?? 0}
                    color="error"
                    max={99}
                    aria-label={supportSummary?.attentionCount ? `${supportSummary.attentionCount} waiting on you` : undefined}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              {!isIconsOnly && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              )}
            </>
          )

          const buttonContent = isLink ? (
            <ListItemButton
              component={RouterLink}
              to={item.path!}
              selected={isActive}
              onClick={isMobile ? onDrawerToggle : undefined}
              sx={navButtonSx}
            >
              {inner}
            </ListItemButton>
          ) : (
            <ListItemButton onClick={handleActionClick} sx={navButtonSx}>
              {inner}
            </ListItemButton>
          )

          return (
            <Box key={item.path ?? item.label}>
              <ListItem disablePadding>
                {isIconsOnly ? (
                  <Tooltip title={item.label} placement="right" arrow>
                    {buttonContent}
                  </Tooltip>
                ) : (
                  buttonContent
                )}
              </ListItem>
              {item.dividerAfter && <Divider sx={{ my: 1 }} />}
            </Box>
          )
        })}
      </List>
    </Box>
  )

  const drawerStyles = {
    width: currentWidth,
    flexShrink: 0,
    transition: 'width 0.2s ease-in-out',
    '& .MuiDrawer-paper': {
      width: currentWidth,
      height: '100vh',
      top: 0,
      boxSizing: 'border-box',
      borderRight: '2px solid',
      borderColor: 'primary.main',
      backgroundImage: `url('/assets/images/Navigation_Background.png')`,
      backgroundSize: '400px',
      backgroundRepeat: 'repeat',
      transition: 'width 0.2s ease-in-out',
      overflowX: 'hidden',
      overflowY: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'primary.main',
        opacity: 0.25,
        zIndex: 0,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'background.paper',
        opacity: 0.8,
        zIndex: 0,
      },
    },
  }

  return (
    <Box
      component="nav"
      sx={{
        width: { md: isHidden ? 0 : currentWidth },
        flexShrink: { md: 0 },
        transition: 'width 0.2s ease-in-out',
        position: 'relative',
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          ...drawerStyles,
          width: SIDEBAR_WIDTH_FULL, // Always full width on mobile
          '& .MuiDrawer-paper': {
            ...drawerStyles['& .MuiDrawer-paper'],
            width: SIDEBAR_WIDTH_FULL,
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      {!isHidden && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: currentWidth,
            flexShrink: 0,
            transition: 'width 0.2s ease-in-out',
            '& .MuiDrawer-paper': {
              ...drawerStyles['& .MuiDrawer-paper'],
            },
          }}
          open
        >
          <Toolbar />
          {drawerContent}
        </Drawer>
      )}

      {/* Scan Code Dialog - rendered once outside drawers to prevent duplicate instances */}
      <ScanCodeDialog open={scanCodeOpen} onClose={() => setScanCodeOpen(false)} />
    </Box>
  )
}
